import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import { cloneDeep, defaultsDeep, shuffle } from 'lodash-es';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartWrapperComponent } from '../chart-wrapper/chart-wrapper.component';

@Component({
    selector: 'vc-column-chart',
    templateUrl: './column-chart.component.html',
    styleUrls: ['./column-chart.component.scss'],
    imports: [HighchartsChartComponent, ChartWrapperComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnChartComponent {
    highcharts: typeof Highcharts = Highcharts;

    private chartInstance?: Highcharts.Chart;

    @Input({ required: true })
    title!: string;

    @Input({ required: true })
    loading!: boolean;

    @Input({ required: true })
    contentHeight!: string;

    @Input()
    formContainer = true;

    @Input({ required: true })
    set options(options: Highcharts.Options) {
        const defaults = cloneDeep(COLUMN_CHART_DEFAULT_OPTIONS);
        const merged = defaultsDeep({}, options, defaults);

        // isolate tooltip completely per chart
        if (options.tooltip) {
            merged.tooltip = { ...options.tooltip };
        }

        this._options = cloneDeep(merged);
        this._updateChart();
    }

    get options() {
        return this._options;
    }
    private _options: Highcharts.Options = { ...COLUMN_CHART_DEFAULT_OPTIONS };

    @Input()
    height!: string;

    onChartInstance(chart: Highcharts.Chart): void {
        this.chartInstance = chart;
        this._updateChart();
    }

    private _updateChart(): void {
        if (!this.chartInstance?.renderer) {
            return;
        }

        this.chartInstance.update(this._options, true, true);
    }
}

export const COLUMN_CHART_DEFAULT_OPTIONS: Highcharts.Options = {
    chart: {
        type: 'column',
        spacingTop: 20,
        spacingBottom: 16,
        borderWidth: 0,
        borderColor: 'transparent',
    },
    subtitle: {
        text: '',
    },
    xAxis: {
        categories: [],
        crosshair: true,
    },
    yAxis: {
        min: 0,
        title: {
            text: '',
        },
    },
    plotOptions: {
        column: {
            groupPadding: 0.1,
            borderWidth: 0,
            borderColor: 'transparent',
            maxPointWidth: 50,
            colors: shuffle([
                'var(--primary-100)',
                'var(--primary-200)',
                'var(--primary-400)',
                'var(--primary-500)',
                'var(--primary-600)',
                'var(--primary-700)',
            ]),
            colorByPoint: true,
        },
    },
    series: [],
    tooltip: {
        format: '{point.name}: <b>{point.y}</b>',
    },
};
